import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetSlipType } from '@bpos';
import { BetState } from '@bpos/v1/my-bets';
import { PicksView } from '@cds/betting-offer/domain-specific';
import { MyBetsVisualizationTrackingConfig, Sitecore, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { BetslipListType, MyBetsImage, MyBetsImages } from '@frontend/sports/types/models/my-bets';
import { HtmlNode, SessionStoreService } from '@frontend/vanilla/core';
import { skip } from 'rxjs';

import { EpcotConfigService } from '../../../common/epcot-config.service';
import { IMyBetsEarlyPayoutSubscription } from '../../../my-bets-base/early-payout/my-bets-earlypayout-base.service';
import { MigrationTabType, MyBetsBetSlip, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { MyBetsFilterConstraint } from '../../models/my-bets-filter-constraints';
import { MyBetsPinningService } from '../../services/my-bets-pinning.service';
import { ReBetFiltersService } from '../re-bet/re-bet-filters.service';
import { ReBetLoaderService } from '../re-bet/re-bet-loader.service';
import { ReBetAvailabilityService } from '../re-bet/re-bet-visibility.service';
import { MyBetsColumnsService, MyBetsGroup } from './my-bets-columns.service';
import { MyBetsListTrackingService } from './my-bets-list-tracking.service';
import { MyBetsListService } from './my-bets-list.service';

@Component({
    selector: 'ms-my-bets-list',
    templateUrl: 'my-bets-list.component.html',
})
export class MyBetsListComponent implements OnInit, OnChanges, OnDestroy {
    @Input() listType: BetslipListType;
    @Input() betslips: MyBetsBetSlip[] = [];

    @Input() myBetsListContext: MyBetsListContext;
    @Input() context?: string;
    @Input() filterConstraint: MyBetsFilterConstraint;
    @Input() earlyPayoutSubscription: IMyBetsEarlyPayoutSubscription;
    @Input() inColumn = false;
    @Input() showMigrationTabs = false;

    @Output() requestMore: EventEmitter<void> = new EventEmitter<void>();
    @Output() filterChanged: EventEmitter<MyBetsFilterConstraint> = new EventEmitter<MyBetsFilterConstraint>();
    @Output() pinningAnimate = new EventEmitter<string>();
    @Output() unpinningAnimate = new EventEmitter<string>();
    @HostBinding('class.migrated-user') isMigratedUser = false;
    emptyBetsImg = this.sitecoreMyBetsConfig.myBetsImages?.find((image: MyBetsImage) => image.name === MyBetsImages.EmptyBets) as MyBetsImage;

    get canBeFiltred(): boolean {
        return !!this.myBetsListContext.showFilter || !!this.myBetsListContext.showMigrationTabs || !!this.myBetsListContext.showPrintButton;
    }

    get hasDateSeparators(): boolean {
        return this.listType === BetslipListType.Settled;
    }

    columns: number;
    groups: MyBetsGroup[];
    private animationTimeout: number;
    private stateFilterConstraintSessionStorageKey = 'stateConstraint';
    private dateRangeFilterConstraintSessionStorageKey = 'dateRangeConstraint';
    isEpcotEnabled: boolean;
    betslipsCount: number;
    private readonly destroyRef = inject(DestroyRef);

    private readonly _doc = inject(DOCUMENT);

    constructor(
        public sitecore: Sitecore,
        private myBetsPinningService: MyBetsPinningService,
        private htmlNode: HtmlNode,
        private myBetsService: MyBetsListService,
        private myBetsColumnsService: MyBetsColumnsService,
        private sessionStorage: SessionStoreService,
        private reBetLoaderService: ReBetLoaderService,
        private reBetAvailability: ReBetAvailabilityService,
        private reBetFiltersService: ReBetFiltersService,
        private changeRef: ChangeDetectorRef,
        private myBetsTrackingService: MyBetsListTrackingService,
        private myBetsVisualizationTrackingConfig: MyBetsVisualizationTrackingConfig,
        private epcotConfigService: EpcotConfigService,
        private sitecoreMyBetsConfig: SitecoreMyBetsConfig,
    ) {}

    ngOnInit(): void {
        this.myBetsPinningService.onPinned.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onBetPinned);
        this.myBetsPinningService.onUnpinned.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onBetUnpinned);

        this.myBetsService
            .asObservable()
            .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((x) => {
                this.groups = this.getGroups(x);
            });

        this.groups = this.getGroups(this.myBetsService.columns);
        this.isMigratedUser = this.showMigrationTabs;
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
        this.betslipsCount = this.betslips.length;
    }

    ngOnChanges(changes: ISimpleChanges<MyBetsListComponent>): void {
        if (changes.listType) {
            this.scrollTop();
        }
        this.groups = this.getGroups(this.myBetsService.columns);

        const newBetslips = changes.betslips;
        const isReBetAvailable = this.reBetAvailability.isAvailable(this.listType);

        if (isReBetAvailable && newBetslips) {
            const previousBetslips: MyBetsBetSlip[] | undefined = newBetslips.previousValue;
            this.getPicks(previousBetslips);
        }

        if (newBetslips && this.myBetsVisualizationTrackingConfig.isEnabled) {
            const betslips = newBetslips.isFirstChange() ? newBetslips.currentValue : this.getCurrentBetslips(newBetslips.previousValue);

            if (betslips.length) {
                this.myBetsTrackingService.trackMyBetsTabsLoad(betslips, this.listType);
            }
        }
    }

    ngOnDestroy(): void {
        window.clearTimeout(this.animationTimeout);
        if (this.listType !== BetslipListType.Settled && this.earlyPayoutSubscription) {
            this.earlyPayoutSubscription.dropEarlyPayoutSubscription();
        }
    }

    private getPicks(previousBetslips: MyBetsBetSlip[] | undefined): void {
        if (!previousBetslips || this.previousBetslipsChanged(previousBetslips)) {
            return this.loadPicks(this.betslips);
        }

        const oldBetslipIds = previousBetslips.map((pv) => pv.betslipId);
        const newBetslips = this.betslips.filter((b) => oldBetslipIds.indexOf(b.betslipId) === -1);
        this.loadPicks(newBetslips.length ? newBetslips : this.betslips);
    }

    private previousBetslipsChanged(previousBetslips: MyBetsBetSlip[]): boolean {
        const betslipIds = this.betslips.flatMap((b) => b.betslipId);

        return !previousBetslips.every((p) => betslipIds.find((id) => id === p.betslipId));
    }

    private loadPicks(betslips: MyBetsBetSlip[]): void {
        let comboBetslips = betslips.filter((b) => b.betType === BetSlipType.Combo && !b.bets.some((bet) => bet.betBuilderInfo));
        comboBetslips = comboBetslips.filter((b) => b.bets.some((bet) => [BetState.Open, BetState.Placed].indexOf(bet.state) > -1));

        comboBetslips.length &&
            this.reBetLoaderService.loadPicks(comboBetslips).then((result) => {
                !!result && this.setReBetPicks(comboBetslips, result);
            });
    }

    private setReBetPicks(betslips: MyBetsBetSlip[], picksView: PicksView): void {
        betslips.forEach((b) => {
            b.reBetPicks = this.reBetFiltersService.getRelatedBets(b.bets, picksView);
            b.listType = this.listType;
        });

        this.groups = this.getGroups(this.myBetsService.columns);
        this.changeRef.detectChanges();
    }

    private getGroups(possibleColumns: number): MyBetsGroup[] {
        this.columns = this.inColumn ? 1 : possibleColumns;

        return this.myBetsColumnsService.getGroups(this.columns, this.betslips, this.hasDateSeparators);
    }

    private handlePinningInDesktop(): void {
        const isMigratedList = this.myBetsListContext.migrationTabType === MigrationTabType.Before;
        const pinnedBetsIds = this.myBetsPinningService.getPinnedBetIds(isMigratedList);
        const pinnedBets = this.betslips.filter((betslip) => pinnedBetsIds.some((pinnedId) => pinnedId === betslip.betslipRealId));

        const unpinnedBets = this.betslips.filter((bet) => !pinnedBetsIds.some((pinnedBetId) => pinnedBetId === bet.betslipRealId));
        this.betslips = [...pinnedBets, ...unpinnedBets];
        this.groups = this.getGroups(this.myBetsService.columns);
    }

    private scrollTop(): void {
        if (!this.htmlNode.hasCssClass('content-right')) {
            // my-bets not on the right.
            if (this._doc.scrollingElement) {
                this._doc.scrollingElement.scrollTop = 0;
            }
        }
    }

    onBetPinned = (betslipId: string) => {
        if (this.columns > 1) {
            this.handlePinningInDesktop();
            this.pinningAnimate.emit(betslipId);

            return;
        }

        this.animatePinning(betslipId).then(() => {
            this.pinningAnimate.emit(betslipId);

            const betslipList = this.groups[0].columnsArray[0].betslips;
            const index = betslipList.findIndex((betslip) => betslip.betslipRealId === betslipId);
            if (index > 0) {
                const moved = betslipList[index];
                betslipList.splice(index, 1);
                betslipList.unshift(moved);
                this.changeRef.detectChanges();
            }
        });
    };

    onBetUnpinned = (betslipId: string) => {
        this.unpinningAnimate.emit(betslipId);
    };

    loadMore(): void {
        this.requestMore.emit();
    }

    get scrollContext() {
        if (this.myBetsListContext.isEventView || typeof this.context !== 'undefined') {
            return 'document';
        } else {
            return 'self';
        }
    }

    private animatePinning(betslipId: string): Promise<void> {
        const betslipElement = this._doc.querySelector(`[data-betslip-pin-id="${betslipId}"]`) as HTMLElement | null;
        if (!betslipElement) {
            return Promise.resolve();
        }

        // Animates up the betslip to be pinned and animates down all other previous siblings.
        // Instead of translatingY for each sibling we use a temporary placeholder, on slower
        // phones is problematic animating multiple elements.
        // Keep timeout delay in sync with .pinning-transition

        return new Promise((resolve) => {
            const listWrapper = this._doc.querySelector('#betslips-list-wrapper ms-my-bets-list-column');
            if (!listWrapper) {
                resolve();

                return;
            }

            const firstChild = listWrapper.firstElementChild as HTMLElement;

            if (firstChild && firstChild !== betslipElement) {
                const pinningPlaceholder = this._doc.createElement('div');
                if (!pinningPlaceholder) {
                    resolve();

                    return;
                }
                pinningPlaceholder.classList.add('pinning-transition');
                pinningPlaceholder.style.height = '0';
                listWrapper.insertBefore(pinningPlaceholder, firstChild);

                const betslipRect = betslipElement.getBoundingClientRect();
                const parentRect = listWrapper.getBoundingClientRect();
                const parentTop = parentRect.top;
                const betslipTop = betslipRect.top;
                const travelDistance = Math.abs(betslipTop - parentTop) - 8;

                betslipElement.style.zIndex = '1';
                betslipElement.style.position = 'absolute';
                betslipElement.style.top = `${travelDistance}px`;
                betslipElement.style.width = `${betslipRect.width}px`;
                betslipElement.style.transform = `translateY(-${travelDistance}px)`;
                pinningPlaceholder.style.height = `${betslipRect.height}px`;
                window.clearTimeout(this.animationTimeout);
                this.animationTimeout = window.setTimeout(() => {
                    betslipElement.style.position = '';
                    betslipElement.style.top = ``;
                    betslipElement.style.width = ``;
                    betslipElement.style.transform = ``;
                    pinningPlaceholder.remove();
                    resolve();
                }, 350);
            }
        });
    }

    onFilterChanged(filter: MyBetsFilterConstraint): void {
        this.filterChanged.emit(filter);
    }

    onResetFilter(): void {
        this.filterConstraint.clean();
        this.filterChanged.emit();

        this.sessionStorage.remove(this.stateFilterConstraintSessionStorageKey);
        this.sessionStorage.remove(this.dateRangeFilterConstraintSessionStorageKey);
    }

    private getCurrentBetslips(previousBetslips: MyBetsBetSlip[] | undefined): MyBetsBetSlip[] {
        const oldBetslipIds = previousBetslips?.map((pv) => pv.betslipId) || [];

        return this.betslips.filter((b) => !oldBetslipIds.includes(b.betslipId));
    }
}
